"use client";

import React from "react";
import { Coffee, BookOpen, Users, Wifi } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const galleryImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_dashboard_preview.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_dashboard_preview.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_dashboard_preview.png-wvc-sizes",
  alt: "DataZen AI automation dashboard interface"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_workflow_builder.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_workflow_builder.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_workflow_builder.png-wvc-sizes",
  alt: "Visual workflow automation builder in action"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_analytics_reporting.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_analytics_reporting.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_analytics_reporting.png-wvc-sizes",
  alt: "Real-time business intelligence and reporting tools"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_document_assistant.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_document_assistant.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_document_assistant.png-wvc-sizes",
  alt: "AI-powered document processing and analysis"
}];
const atmosphereFeatures = [{
  icon: <Wifi className="size-6 text-primary" />,
  title: "AI Workflow Automation",
  description: "Eliminate repetitive tasks with intelligent triggers and actions. Our platform orchestrates complex business processes with enterprise-grade reliability."
}, {
  icon: <BookOpen className="size-6 text-primary" />,
  title: "Document Intelligence",
  description: "Transform unstructured data into actionable insights. Our AI assistants read, summarize, and extract key information from your business documents instantly."
}, {
  icon: <Users className="size-6 text-primary" />,
  title: "Unified Dashboards",
  description: "Centralize your operations in a single mission control center. Monitor performance, track automations, and gain deep data insights in real-time."
}];
export default function SeeDatazenInAction() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const carouselReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const featuresReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="259979" id="system" className="bg-[hsl(210,20%,8%)] py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 bg-primary/10 text-primary border-primary/20">
            DataZen System
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            DataZen Live System
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            Experience the power of premium AI automation. Our interface is designed 
            for clarity, speed, and enterprise-level control over your most critical 
            business workflows.
          </p>
        </div>

        {}
        <div ref={carouselReveal.ref} className={`mb-16 md:mb-24 transition-all duration-1000 delay-200 ease-out ${carouselReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-2 md:-ml-4">
              {galleryImages.map((image, index) => <CarouselItem key={index} data-index={index} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="overflow-hidden rounded-lg shadow-lg border border-white/10 bg-card/50 backdrop-blur-md group ring-1 ring-primary/20">
                    <AspectRatio ratio={16 / 9}>
                      <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
                    </AspectRatio>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="left-4 bg-background/40 backdrop-blur-md border-white/10 hover:bg-primary/20 text-primary" />
              <CarouselNext className="right-4 bg-background/40 backdrop-blur-md border-white/10 hover:bg-primary/20 text-primary" />
            </div>
          </Carousel>
        </div>

        {}
        <div ref={featuresReveal.ref} className={`grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 transition-all duration-1000 delay-300 ease-out ${featuresReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {atmosphereFeatures.map((feature, index) => <div key={index} data-index={index} className="flex flex-col items-center text-center p-6 rounded-xl bg-white/5 backdrop-blur-sm shadow-md border border-white/10 hover:border-primary/40 transition-all duration-300 group">
              <div className="p-4 bg-primary/10 rounded-lg mb-6 group-hover:shadow-[0_0_20px_rgba(0,255,255,0.2)] transition-all">
                {feature.icon}
              </div>
              <h3 className="text-xl font-default font-semibold text-foreground mb-3">
                {feature.title}
              </h3>
              <p className="text-muted-foreground font-default leading-relaxed">
                {feature.description}
              </p>
            </div>)}
        </div>

        {}
        <div className="mt-16 text-center">
          <Button size="lg" className="rounded-md font-default bg-gradient-to-r from-[hsl(180,100%,50%)] to-[hsl(190,100%,40%)] hover:scale-105 transition-transform text-[hsl(180,100%,10%)] font-bold" onClick={() => document.getElementById('contactgetintouch')?.scrollIntoView({
          behavior: 'smooth'
        })}>
            Book Free Consultation
          </Button>
        </div>
      </div>
    </section>;
}